import { useState, useCallback } from "react";
import clientsApi from "../API/clientsApi";

export const useClientsApi = (showNotificationArg) => {
  const [loading, setLoading] = useState(false);
  const [clients, setClients] = useState([]);
  const [client, setClient] = useState(null);
    const [error, setError] = useState("");


  // Use passed showNotification or fallback to local (for backward compatibility)
  let showNotification = showNotificationArg;
  if (!showNotification) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    showNotification = require("./useNotification").default().showNotification;
  }

  // Get all clients
  const fetchClients = useCallback(
    async (status = 1) => {
      setLoading(true);
      try {
        const response = await clientsApi.getAll(status);
        if (response.error) {
          showNotification(response.error, "error");
          setClients([]);
          return { success: false, error: response.error };
        }
        setClients(response.data || response || []);
        return { success: true, data: response.data || response };
      } catch (error) {
        console.error("Error fetching clients:", error);
        showNotification("Error fetching clients", "error");
        return {
          success: false,
          error: error.response?.data?.message || "Failed to fetch clients",
        };
      } finally {
        setLoading(false);
      }
    },
    [showNotification]
  );

  // Get client by ID
  const fetchClientById = useCallback(
    async (id) => {
      setLoading(true);
      try {
        const response = await clientsApi.getById(id);
        setClient(response.data);
        return { success: true, data: response.data };
      } catch (error) {
        console.error("Error fetching client details:", error);
        showNotification("Error fetching client details", "error");
        return {
          success: false,
          error:
            error.response?.data?.message || "Failed to fetch client details",
        };
      } finally {
        setLoading(false);
      }
    },
    [showNotification]
  );

  // Create new client
  const createClient = useCallback(
    async (clientData) => {
      setLoading(true);
      try {
        const response = await clientsApi.create(clientData);

        if (response.success) {
          showNotification("Client created successfully", "success");
        } else if (response.error && response.error.statusCode === 409) {
          showNotification(response.error.message, "error");
          return { success: false };
        }
        return { success: true, data: response.data };
      } catch (error) {
        console.error("Error creating client:", error);
        showNotification(
          error.response?.data?.message ||
            error.message ||
            "Failed to create client",
          "error"
        );
        return {
          success: false,
          error: error.response?.data?.message || "Failed to create client",
        };
      } finally {
        setLoading(false);
      }
    },
    [showNotification]
  );

  // Update client details
  const updateClientDetails = useCallback(
    async (id, clientData) => {
      setLoading(true);
      try {
        await clientsApi.updateDetails(id, clientData);
        showNotification("Client details updated successfully", "success");
        return { success: true };
      } catch (error) {
        console.error("Error updating client details:", error);
        showNotification(
          error.response?.data?.message || "Failed to update client details",
          "error"
        );
        return {
          success: false,
          error:
            error.response?.data?.message || "Failed to update client details",
        };
      } finally {
        setLoading(false);
      }
    },
    [showNotification]
  );

  // Update client fees
  const updateClientFees = useCallback(
    async (id, feesData) => {
      setLoading(true);
      try {
        await clientsApi.updateFees(id, feesData);
        showNotification("Client fees updated successfully", "success");
        return { success: true };
      } catch (error) {
        console.error("Error updating client fees:", error);
        showNotification(
          error.response?.data?.message || "Failed to update client fees",
          "error"
        );
        return {
          success: false,
          error:
            error.response?.data?.message || "Failed to update client fees",
        };
      } finally {
        setLoading(false);
      }
    },
    [showNotification]
  );

  // Update client web details
  const updateClientWebDetails = useCallback(
    async (id, webDetailsData) => {
      setLoading(true);
      try {
        await clientsApi.updateWebDetails(id, webDetailsData);
        showNotification("Client web details updated successfully", "success");
        return { success: true };
      } catch (error) {
        console.error("Error updating client web details:", error);
        showNotification(
          error.response?.data?.message ||
            "Failed to update client web details",
          "error"
        );
        return {
          success: false,
          error:
            error.response?.data?.message ||
            "Failed to update client web details",
        };
      } finally {
        setLoading(false);
      }
    },
    [showNotification]
  );

  // Update client credit bundles
  const updateClientCreditBundles = useCallback(
    async (id, creditBundles) => {
      setLoading(true);
      try {
        await clientsApi.updateCreditBundles(id, creditBundles);
        showNotification(
          "Client credit bundles updated successfully",
          "success"
        );
        return { success: true };
      } catch (error) {
        console.error("Error updating client credit bundles:", error);
        showNotification(
          error.response?.data?.message ||
            "Failed to update client credit bundles",
          "error"
        );
        return {
          success: false,
          error:
            error.response?.data?.message ||
            "Failed to update client credit bundles",
        };
      } finally {
        setLoading(false);
      }
    },
    [showNotification]
  );

  // Get clients dropdown
  const fetchClientsDropdown = useCallback(async () => {
    try {
      const response = await clientsApi.getDropdown();
      return { success: true, data: response.data || response };
    } catch (error) {
      console.error("Error fetching clients dropdown:", error);
      showNotification("Error fetching clients dropdown", "error");
      return {
        success: false,
        error:
          error.response?.data?.message || "Failed to fetch clients dropdown",
      };
    }
  }, [showNotification]);

  // Delete client
  const deleteClient = useCallback(
    async (id) => {
      setLoading(true);
      try {
        await clientsApi.delete(id);
        showNotification("Client deleted successfully", "success");
        return { success: true };
      } catch (error) {
        console.error("Error deleting client:", error);
        showNotification(
          error.response?.data?.message || "Failed to delete client",
          "error"
        );
        return {
          success: false,
          error: error.response?.data?.message || "Failed to delete client",
        };
      } finally {
        setLoading(false);
      }
    },
    [showNotification]
  );
  const getConsumerAcknowledgement = useCallback(async (clientId) => {
      setLoading(true);
      setError(null);
      try {
        const result = await clientsApi.getConsumerAcknowledgement(clientId);
        if (result.error) {
          setError(result.error);
          return null;
        }
        return result;
      } catch (err) {
        setError("Failed to fetch acknowledgement");
        return null;
      } finally {
        setLoading(false);
      }
    }, []);
  
    const createOrUpdateConsumerAcknowledgement = useCallback(async (ClientId, payload) => {
      setLoading(true);
      setError(null);
      try {
        const result = await clientsApi.createOrUpdateConsumerAcknowledgement(ClientId, payload);
        if (result.error) {
          setError(result.error);
          return null;
        }
        return result;
      } catch (err) {
        setError("Failed to save acknowledgement");
        return null;
      } finally {
        setLoading(false);
      }
    }, []);

    const updateConsumerAcknowledgement = useCallback(async (ClientId, payload) => {
      setLoading(true);
      setError(null);
      try {
        const result = await clientsApi.updateConsumerAcknowledgement(ClientId, payload);
        if (result.error) {
          setError(result.error);
          return null;
        }
        return result;
      } catch (err) {
        setError("Failed to update acknowledgement");
        return null;
      } finally {
        setLoading(false);
      }
    }, []);

  return {
    loading,
    clients,
    client,
    fetchClients,
    fetchClientById,
    createClient,
    updateClientDetails,
    updateClientFees,
    updateClientWebDetails,
    updateClientCreditBundles,
    fetchClientsDropdown,
    deleteClient,
    getConsumerAcknowledgement,
    createOrUpdateConsumerAcknowledgement,
    updateConsumerAcknowledgement,
  };
};

export default useClientsApi;
